import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import CameraPhotoCapture, { CameraPhotoCaptureHandle } from '../../components/visitor-logs/CameraPhotoCapture';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Alert from '../../components/ui/Alert';
import Loading from '../../components/ui/Loading';
import { ArrowLeft, X, Camera as CameraIcon, Loader2, ParkingCircle } from 'lucide-react';
import { visitorLogService, locationService, parkingService } from '../../services';
import { cameraService } from '../../services/camera.service';
import { useVisitorLogPresignedUrl } from '../../hooks';
import type { Location, Camera, ParkingLot, ParkingSpot } from '../../types';

const EditEntryExitPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Location[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Capture camera states
  const [selectedIdCamera, setSelectedIdCamera] = useState<string>('');
  const [selectedFaceCamera, setSelectedFaceCamera] = useState<string>('');
  const [selectedLicensePlateCamera, setSelectedLicensePlateCamera] = useState<string>('');
  const [capturedIdImage, setCapturedIdImage] = useState<string | null>(null);
  const [capturedFaceImage, setCapturedFaceImage] = useState<string | null>(null);
  const [capturedLicensePlateImage, setCapturedLicensePlateImage] = useState<string | null>(null);
  const [idPhotoFile, setIdPhotoFile] = useState<File | null>(null);
  const [facePhotoFile, setFacePhotoFile] = useState<File | null>(null);
  const [licensePlatePhotoFile, setLicensePlatePhotoFile] = useState<File | null>(null);

  // Vehicle angle photos
  const [vehicleFrontImage, setVehicleFrontImage] = useState<string | null>(null);
  const [vehicleRightImage, setVehicleRightImage] = useState<string | null>(null);
  const [vehicleLeftImage, setVehicleLeftImage] = useState<string | null>(null);
  const [vehicleBackImage, setVehicleBackImage] = useState<string | null>(null);
  const [vehicleFrontFile, setVehicleFrontFile] = useState<File | null>(null);
  const [vehicleRightFile, setVehicleRightFile] = useState<File | null>(null);
  const [vehicleLeftFile, setVehicleLeftFile] = useState<File | null>(null);
  const [vehicleBackFile, setVehicleBackFile] = useState<File | null>(null);
  const [isCapturingVehicleAngle, setIsCapturingVehicleAngle] = useState<string | null>(null);

  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [uploadingImages, setUploadingImages] = useState(false);

  // Existing images
  const [existingImages, setExistingImages] = useState({
    log_license_image: '',
    log_image: '',
    log_url: '',
    vehicle_front: '',
    vehicle_right: '',
    vehicle_left: '',
    vehicle_back: '',
  });

  // Parking state
  const [parkingLots, setParkingLots] = useState<ParkingLot[]>([]);
  const [availableSpots, setAvailableSpots] = useState<ParkingSpot[]>([]);
  const [selectedParkingLot, setSelectedParkingLot] = useState<string>('');
  const [selectedParkingSpot, setSelectedParkingSpot] = useState<string>('');
  const [selectedParkingSpot2, setSelectedParkingSpot2] = useState<string>(''); // Second spot for truck with trailer
  const [loadingParkingLots, setLoadingParkingLots] = useState(false);
  const [loadingSpots, setLoadingSpots] = useState(false);
  const [originalParkingSpotId, setOriginalParkingSpotId] = useState<string>('');
  const [originalParkingSpotId2, setOriginalParkingSpotId2] = useState<string>(''); // Original second spot
  const [parkingSummary, setParkingSummary] = useState<{ available: number; total: number } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    location_id: '',
    log_access_type: 'ENTRY',
    log_operator_type: 'EMPLOYEE',
    log_capture_type: 'MANUAL',
    has_vehicle: '1',
    log_license_plate: '',
    log_vehicle_type: '',
    log_vehicle_make: '',
    log_vehicle_color: '',
    log_vehicle_model: '',
    log_truck: '',
    log_trailer: '',
    log_trailer_plate: '',
    log_damages: '',
    log_safety_vest: '',
    log_external_driver_name: '',
    log_driver_license_no: '',
    log_driver_email: '',
    log_visitor_company: '',
    log_visitor_phone_number: '',
    log_visitor_purpose: '',
    log_note: '',
    access_point: '',
    inspection_status: '',
    inspected_by: '',
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchLocations();
    if (id) {
      fetchLog(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchLocations = async () => {
    try {
      const response = await locationService.getLocations();
      console.log('Locations loaded:', response.data?.length, 'locations');
      setLocations(response.data || []);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    }
  };

  const fetchLog = useCallback(async (logId: string) => {
    try {
      setIsLoading(true);
      const log: any = await visitorLogService.getVisitorLogById(logId);
      
      console.log('Fetched log data:', log);
      console.log('location_id:', log.location_id);
      console.log('log_access_type:', log.log_access_type);
      console.log('log_operator_type:', log.log_operator_type);
      console.log('log_capture_type:', log.log_capture_type);
      console.log('has_vehicle:', log.has_vehicle);
      
      // Parse vehicle_details if it exists
      let vehicleDetails: any = {};
      if (log.vehicle_details) {
        try {
          vehicleDetails = JSON.parse(log.vehicle_details);
        } catch (err) {
          console.error('Failed to parse vehicle_details:', err);
        }
      }

      // Populate form with existing data
      const formDataToSet = {
        location_id: log.location_id?.toString() || '',
        log_access_type: log.log_access_type || 'ENTRY',
        log_operator_type: log.log_operator_type || 'EMPLOYEE',
        log_capture_type: log.log_capture_type || 'MANUAL',
        has_vehicle: log.has_vehicle?.toString() || '1',
        log_license_plate: log.log_license_plate || '',
        log_vehicle_type: log.log_vehicle_type || '',
        log_vehicle_make: log.log_vehicle_make || '',
        log_vehicle_color: log.log_vehicle_color || '',
        log_vehicle_model: log.log_vehicle_model || '',
        log_truck: log.log_truck || '',
        log_trailer: log.log_trailer || '',
        log_trailer_plate: log.log_trailer_plate || '',
        log_damages: log.log_damages || '',
        log_safety_vest: log.log_safety_vest || '',
        log_external_driver_name: log.log_external_driver_name || '',
        log_driver_license_no: log.log_driver_license_no || '',
        log_driver_email: log.log_driver_email || '',
        log_visitor_company: log.log_visitor_company || '',
        log_visitor_phone_number: log.log_visitor_phone_number || '',
        log_visitor_purpose: log.log_visitor_purpose || '',
        log_note: log.log_note || '',
        access_point: vehicleDetails.access_point || '',
        inspection_status: vehicleDetails.inspection_status || '',
        inspected_by: vehicleDetails.inspected_by || '',
      };
      
      console.log('Setting formData:', JSON.stringify(formDataToSet, null, 2));
      setFormData(formDataToSet);

      // Store existing images
      setExistingImages({
        log_license_image: log.log_license_image || '',
        log_image: log.log_image || '',
        log_url: log.log_url || '',
        vehicle_front: vehicleDetails.log_vehicle_image_front || '',
        vehicle_right: vehicleDetails.log_vehicle_image_right || '',
        vehicle_left: vehicleDetails.log_vehicle_image_left || '',
        vehicle_back: vehicleDetails.log_vehicle_image_back || '',
      });

      // Set previews to existing images
      console.log('Setting image previews:');
      console.log('ID Image (log_license_image):', log.log_license_image);
      console.log('Face Image (log_image):', log.log_image);
      console.log('License Plate (log_url):', log.log_url);
      console.log('Vehicle images:', vehicleDetails);
      
      if (log.log_license_image) setCapturedIdImage(log.log_license_image);
      if (log.log_image) setCapturedFaceImage(log.log_image);
      if (log.log_url) setCapturedLicensePlateImage(log.log_url);
      if (vehicleDetails.log_vehicle_image_front) setVehicleFrontImage(vehicleDetails.log_vehicle_image_front);
      if (vehicleDetails.log_vehicle_image_right) setVehicleRightImage(vehicleDetails.log_vehicle_image_right);
      if (vehicleDetails.log_vehicle_image_left) setVehicleLeftImage(vehicleDetails.log_vehicle_image_left);
      if (vehicleDetails.log_vehicle_image_back) setVehicleBackImage(vehicleDetails.log_vehicle_image_back);

      // Fetch cameras for the location
      if (log.location_id) {
        fetchCameras(log.location_id.toString());
        fetchParkingLots(log.location_id.toString());
      }

      // Set parking spot if exists
      console.log('Parking data from log:', { parking_lot_id: log.parking_lot_id, parking_spot_id: log.parking_spot_id, parking_spot_id_2: log.parking_spot_id_2 });
      if (log.parking_lot_id) {
        console.log('Setting parking lot:', log.parking_lot_id);
        setSelectedParkingLot(log.parking_lot_id.toString());
        // Fetch spots for this lot and set the selected ones (include both spots)
        fetchParkingSpots(log.parking_lot_id.toString(), log.parking_spot_id?.toString() || '', log.parking_spot_id_2?.toString() || '');
      }
      if (log.parking_spot_id) {
        console.log('Setting parking spot:', log.parking_spot_id);
        setSelectedParkingSpot(log.parking_spot_id.toString());
        setOriginalParkingSpotId(log.parking_spot_id.toString());
      }
      // Set second parking spot if exists (for truck with trailer)
      if (log.parking_spot_id_2) {
        console.log('Setting second parking spot:', log.parking_spot_id_2);
        setSelectedParkingSpot2(log.parking_spot_id_2.toString());
        setOriginalParkingSpotId2(log.parking_spot_id_2.toString());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch log');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchCameras = async (locationId: string) => {
    if (!locationId) {
      setCameras([]);
      return;
    }

    try {
      const response = await cameraService.getCamerasByLocation(parseInt(locationId), { size: 100 });
      console.log('Cameras for location:', response.data);
      response.data?.forEach((cam: any) => {
        console.log(`Camera: ${cam.name || cam.camera_location}, Purpose: ${cam.camera_purpose}`);
      });
      setCameras(response.data || []);
    } catch (err) {
      console.error('Failed to fetch cameras:', err);
    }
  };

  // Parking lot functions
  const fetchParkingLots = async (locationId: string) => {
    if (!locationId) {
      setParkingLots([]);
      return;
    }
    try {
      setLoadingParkingLots(true);
      const response = await parkingService.getLots(parseInt(locationId));
      console.log('Parking lots for edit page:', response);
      const lots = Array.isArray(response) ? response : (response.data || []);
      setParkingLots(lots);
    } catch (err) {
      console.error('Failed to fetch parking lots:', err);
      setParkingLots([]);
    } finally {
      setLoadingParkingLots(false);
    }
  };

  const fetchParkingSpots = async (lotId: string, currentSpotId?: string, currentSpotId2?: string) => {
    console.log('fetchParkingSpots called with lotId:', lotId, 'currentSpotId:', currentSpotId, 'currentSpotId2:', currentSpotId2);
    if (!lotId) {
      setAvailableSpots([]);
      return;
    }
    try {
      setLoadingSpots(true);
      
      // For edit, we want to show all spots (both available AND the currently assigned ones)
      const response = await parkingService.getSpots(parseInt(lotId));
      console.log('All spots for edit:', response);
      const allSpots = Array.isArray(response) ? response : (response.data || []);
      
      // Filter to available spots + the current spots (even if occupied)
      const spotsToShow = allSpots.filter((spot: any) => {
        const spotId = spot.id || spot.spot_id;
        return spot.status === 'available' || spotId?.toString() === currentSpotId || spotId?.toString() === currentSpotId2;
      });
      
      setAvailableSpots(spotsToShow);
      
      // Get summary
      try {
        const summaryResponse = await parkingService.getSummary(parseInt(lotId));
        const summary = (summaryResponse as any).data || summaryResponse;
        setParkingSummary({ available: summary.available || 0, total: summary.total_spots || summary.total || 0 });
      } catch {
        setParkingSummary(null);
      }
    } catch (err) {
      console.error('Failed to fetch parking spots:', err);
      setAvailableSpots([]);
    } finally {
      setLoadingSpots(false);
    }
  };

  const handleParkingLotChange = (lotId: string) => {
    console.log('handleParkingLotChange called with:', lotId);
    setSelectedParkingLot(lotId);
    setSelectedParkingSpot('');
    setSelectedParkingSpot2('');
    if (lotId) {
      fetchParkingSpots(lotId, originalParkingSpotId, originalParkingSpotId2);
    } else {
      setAvailableSpots([]);
      setParkingSummary(null);
    }
  };

  const handleParkingSpotChange = (spotId: string) => {
    setSelectedParkingSpot(spotId);
    // Clear second spot if first spot is cleared
    if (!spotId) {
      setSelectedParkingSpot2('');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    // Uppercase truck/trailer fields
    const processedValue = ['log_truck', 'log_trailer', 'log_trailer_plate'].includes(name) 
      ? value.toUpperCase() 
      : value;
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Fetch cameras when location changes
    if (name === 'location_id') {
      setSelectedIdCamera(''); setSelectedFaceCamera(''); setSelectedLicensePlateCamera('');
      fetchCameras(value);
    }
  };

  const licensePlateCaptureRef = useRef<CameraPhotoCaptureHandle>(null);
  const captureUrls = useRef<Record<string, string>>({});
  useEffect(() => () => {
    Object.values(captureUrls.current).forEach(url => URL.revokeObjectURL(url));
  }, []);

  const photoSetters = {
    id: [setCapturedIdImage, setIdPhotoFile] as const,
    face: [setCapturedFaceImage, setFacePhotoFile] as const,
    license_plate: [setCapturedLicensePlateImage, setLicensePlatePhotoFile] as const,
    front: [setVehicleFrontImage, setVehicleFrontFile] as const,
    right: [setVehicleRightImage, setVehicleRightFile] as const,
    left: [setVehicleLeftImage, setVehicleLeftFile] as const,
    back: [setVehicleBackImage, setVehicleBackFile] as const,
  };
  const applyCapture = (type: keyof typeof photoSetters, file: File) => {
    if (captureUrls.current[type]) URL.revokeObjectURL(captureUrls.current[type]);
    const url = URL.createObjectURL(file);
    captureUrls.current[type] = url;
    const [setImage, setFile] = photoSetters[type];
    setImage(url); setFile(file);
  };
  const captureVehicleAngle = async (angle: 'front' | 'right' | 'left' | 'back') => {
    if (!selectedLicensePlateCamera || !licensePlateCaptureRef.current) {
      setError('Select a license plate camera and wait for its preview first.');
      return;
    }
    setIsCapturingVehicleAngle(angle); setError(null);
    try {
      const blob = await licensePlateCaptureRef.current.capture();
      applyCapture(angle, new File([blob], `vehicle-${angle}-${Date.now()}.jpg`, { type: blob.type }));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unable to capture this vehicle photo.');
    } finally { setIsCapturingVehicleAngle(null); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});

    try {
      setIsSaving(true);
      setUploadingImages(true);

      const imageUrls: Record<string, string> = {};

      // Collect images that need to be uploaded (new images only)
      const imageUploads: Array<{ file: File; logType: string; key: string }> = [];
      
      if (idPhotoFile) {
        imageUploads.push({ file: idPhotoFile, logType: 'id', key: 'log_license_image' });
      }
      if (facePhotoFile) {
        imageUploads.push({ file: facePhotoFile, logType: 'face', key: 'log_image' });
      }
      if (licensePlatePhotoFile) {
        imageUploads.push({ file: licensePlatePhotoFile, logType: 'license_plate', key: 'log_url' });
      }
      if (vehicleFrontFile) {
        imageUploads.push({ file: vehicleFrontFile, logType: 'vehicle_front', key: 'log_vehicle_image_front' });
      }
      if (vehicleRightFile) {
        imageUploads.push({ file: vehicleRightFile, logType: 'vehicle_right', key: 'log_vehicle_image_right' });
      }
      if (vehicleLeftFile) {
        imageUploads.push({ file: vehicleLeftFile, logType: 'vehicle_left', key: 'log_vehicle_image_left' });
      }
      if (vehicleBackFile) {
        imageUploads.push({ file: vehicleBackFile, logType: 'vehicle_back', key: 'log_vehicle_image_back' });
      }

      // Upload new images sequentially
      for (let i = 0; i < imageUploads.length; i++) {
        const { file, logType, key } = imageUploads[i];
        setUploadProgress(`Uploading image ${i + 1} of ${imageUploads.length}...`);
        
        try {
          const locationId = formData.location_id ? parseInt(formData.location_id) : undefined;
          const uploadResponse = await visitorLogService.uploadImage(file, logType as any, locationId);
          imageUrls[key] = uploadResponse.url;
        } catch (uploadErr) {
          console.error(`Failed to upload ${logType}:`, uploadErr);
          throw new Error(`Failed to upload ${logType} image`);
        }
      }

      setUploadingImages(false);
      setUploadProgress('Updating log entry...');

      // Build payload with new or existing image URLs
      const payload: any = {
        location_id: parseInt(formData.location_id),
        log_access_type: formData.log_access_type,
        log_operator_type: formData.log_operator_type,
        log_capture_type: formData.log_capture_type,
        has_vehicle: formData.has_vehicle,
        log_license_plate: formData.log_license_plate || undefined,
        log_vehicle_type: formData.log_vehicle_type || undefined,
        log_vehicle_make: formData.log_vehicle_make || undefined,
        log_vehicle_color: formData.log_vehicle_color || undefined,
        log_vehicle_model: formData.log_vehicle_model || undefined,
        log_truck: formData.log_truck ? formData.log_truck.replace(/\s+/g, '').toUpperCase() : undefined,
        log_trailer: formData.log_trailer ? formData.log_trailer.replace(/\s+/g, '').toUpperCase() : undefined,
        log_trailer_plate: formData.log_trailer_plate ? formData.log_trailer_plate.replace(/\s+/g, '').toUpperCase() : undefined,
        log_damages: formData.log_damages || undefined,
        log_safety_vest: formData.log_safety_vest || undefined,
        log_external_driver_name: formData.log_external_driver_name || undefined,
        log_driver_license_no: formData.log_driver_license_no || undefined,
        log_driver_email: formData.log_driver_email || undefined,
        log_visitor_company: formData.log_visitor_company || undefined,
        log_visitor_phone_number: formData.log_visitor_phone_number || undefined,
        log_visitor_purpose: formData.log_visitor_purpose || undefined,
        log_note: formData.log_note || undefined,
      };

      // Add image URLs (new or existing)
      if (imageUrls.log_license_image) {
        payload.log_license_image = imageUrls.log_license_image;
      } else if (existingImages.log_license_image) {
        payload.log_license_image = existingImages.log_license_image;
      }

      if (imageUrls.log_image) {
        payload.log_image = imageUrls.log_image;
      } else if (existingImages.log_image) {
        payload.log_image = existingImages.log_image;
      }

      if (imageUrls.log_url) {
        payload.log_url = imageUrls.log_url;
      } else if (existingImages.log_url) {
        payload.log_url = existingImages.log_url;
      }

      // Build vehicle_details JSON
      const vehicleDetailsObj: any = {
        access_point: formData.access_point || '',
        inspection_status: formData.inspection_status || '',
        inspected_by: formData.inspected_by || '',
        log_vehicle_image_front: imageUrls.log_vehicle_image_front || existingImages.vehicle_front || '',
        log_vehicle_image_right: imageUrls.log_vehicle_image_right || existingImages.vehicle_right || '',
        log_vehicle_image_left: imageUrls.log_vehicle_image_left || existingImages.vehicle_left || '',
        log_vehicle_image_back: imageUrls.log_vehicle_image_back || existingImages.vehicle_back || '',
      };

      payload.vehicle_details = JSON.stringify(vehicleDetailsObj);

      // Add parking information if selected
      if (selectedParkingLot && selectedParkingSpot) {
        const selectedSpot = availableSpots.find(s => {
          const sId = (s as any).id || s.spot_id;
          return sId?.toString() === selectedParkingSpot;
        });
        if (selectedSpot) {
          payload.parking_lot_id = parseInt(selectedParkingLot);
          payload.parking_spot_id = parseInt(selectedParkingSpot);
          payload.parking_spot_number = selectedSpot.spot_number;
          
          // Add second spot for truck with trailer
          if (selectedParkingSpot2) {
            const selectedSpot2 = availableSpots.find(s => {
              const sId = (s as any).id || s.spot_id;
              return sId?.toString() === selectedParkingSpot2;
            });
            if (selectedSpot2) {
              payload.parking_spot_id_2 = parseInt(selectedParkingSpot2);
              payload.parking_spot_number_2 = selectedSpot2.spot_number;
            }
          }
        }
      } else {
        // Clear parking if removed
        payload.parking_lot_id = undefined;
        payload.parking_spot_id = undefined;
        payload.parking_spot_number = undefined;
        payload.parking_spot_id_2 = undefined;
        payload.parking_spot_number_2 = undefined;
      }

      console.log('Update payload:', payload);

      await visitorLogService.updateVisitorLog(id!, payload);

      // Handle parking spot changes
      const isTruckWithTrailer = formData.log_vehicle_type === 'Truck with Trailer';
      const isTruck = formData.log_vehicle_type === 'Truck Only' || isTruckWithTrailer;
      const spot1Changed = selectedParkingSpot !== originalParkingSpotId;
      const spot2Changed = selectedParkingSpot2 !== originalParkingSpotId2;
      
      if (spot1Changed || spot2Changed) {
        try {
          const trailerPlate = formData.log_trailer_plate ? formData.log_trailer_plate.toUpperCase() : undefined;
          
          // Release old spots if there were any - just send spot_ids, backend reads plates
          const oldSpotIds: number[] = [];
          if (originalParkingSpotId) oldSpotIds.push(parseInt(originalParkingSpotId));
          if (originalParkingSpotId2) oldSpotIds.push(parseInt(originalParkingSpotId2));
          
          if (oldSpotIds.length > 0 && selectedParkingLot) {
            await parkingService.releaseMultipleSpots(parseInt(selectedParkingLot), {
              spot_ids: oldSpotIds
            });
            console.log('Released old parking spot(s):', oldSpotIds);
          }
          
          // Occupy new spots if selected
          if (selectedParkingSpot && selectedParkingLot) {
            if (isTruckWithTrailer && selectedParkingSpot2) {
              // Truck with trailer - 2 spots
              await parkingService.occupyMultipleSpots(parseInt(selectedParkingLot), {
                vehicle_plate: formData.log_license_plate.toUpperCase(),
                trailer_plate: trailerPlate,
                vehicle_type: 'truck',
                driver_name: formData.log_external_driver_name || undefined,
                spot_ids: [parseInt(selectedParkingSpot), parseInt(selectedParkingSpot2)]
              });
            } else if (isTruck) {
              // Truck only - 1 spot
              await parkingService.occupyMultipleSpots(parseInt(selectedParkingLot), {
                vehicle_plate: formData.log_license_plate.toUpperCase(),
                trailer_plate: trailerPlate,
                vehicle_type: 'truck',
                driver_name: formData.log_external_driver_name || undefined,
                spot_ids: [parseInt(selectedParkingSpot)]
              });
            } else {
              // Car - single spot park
              await parkingService.parkInSpot(parseInt(selectedParkingSpot), {
                vehicle_plate: formData.log_license_plate.toUpperCase(),
                vehicle_type: 'car',
                driver_name: formData.log_external_driver_name || undefined
              });
            }
            console.log('Occupied new parking spot(s)');
          }
        } catch (parkingErr) {
          console.error('Failed to update parking spot:', parkingErr);
          // Don't fail the entire operation
        }
      }

      navigate('/visitor-logs');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update log');
      console.error('Update error:', err);
    } finally {
      setIsSaving(false);
      setUploadingImages(false);
      setUploadProgress('');
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-64">
          <Loading size="lg" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button type="button"
              variant="ghost"
              leftIcon={<ArrowLeft size={20} />}
              onClick={() => navigate('/visitor-logs')}
            >
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">Edit Entry/Exit Log</h1>
              <p className="text-text-muted">Update entry/exit log information</p>
            </div>
          </div>
        </div>

        {error && (
          <Alert variant="error" message={error} onClose={() => setError(null)} />
        )}

        {uploadingImages && (
          <Alert variant="info" message={uploadProgress} />
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <h3 className="text-lg font-semibold text-text-primary mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Location <span className="text-red-500">*</span>
                </label>
                <select
                  key={`location-${formData.location_id}`}
                  name="location_id"
                  value={formData.location_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background-elevated border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                >
                  <option value="">Select Location</option>
                  {locations.map((location) => (
                    <option key={location.ycl_id} value={location.ycl_id.toString()}>
                      {location.ycl_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Access Type <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="log_access_type"
                      value="ENTRY"
                      checked={formData.log_access_type === 'ENTRY'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-text-primary">Entry</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="log_access_type"
                      value="EXIT"
                      checked={formData.log_access_type === 'EXIT'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-text-primary">Exit</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Entry/Exit By <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="log_operator_type"
                      value="EMPLOYEE"
                      checked={formData.log_operator_type === 'EMPLOYEE'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-text-primary">Employee</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="log_operator_type"
                      value="SUBCONTRACTOR"
                      checked={formData.log_operator_type === 'SUBCONTRACTOR'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-text-primary">Subcontractor</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="log_operator_type"
                      value="VISITOR"
                      checked={formData.log_operator_type === 'VISITOR'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-text-primary">Visitor</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Capture Type
                </label>
                <select
                  key={`capture-${formData.log_capture_type}`}
                  name="log_capture_type"
                  value={formData.log_capture_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background-elevated border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="MANUAL">Manual</option>
                  <option value="AUTO">Auto</option>
                </select>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.has_vehicle === '1'}
                    onChange={e => setFormData(prev => ({ ...prev, has_vehicle: e.target.checked ? '1' : '0' }))}
                    className="mr-2 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-text-secondary">Has Vehicle</span>
                </label>
              </div>
            </div>
          </Card>

          {/* Capture Section */}
          {formData.location_id && cameras.length > 0 && (
            <Card className="mb-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                <CameraIcon className="w-5 h-5" />
                Capture Photos
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* ID Capture */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Capture ID
                  </label>
                  <select
                    value={selectedIdCamera}
                    onChange={e => setSelectedIdCamera(e.target.value)}
                    className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                  >
                    <option value="">Select ID Camera</option>
                    {cameras
                      .filter(camera => camera.camera_purpose === 'id_capture')
                      .map(camera => (
                        <option key={camera.camera_id} value={camera.camera_id}>
                          {camera.name || camera.camera_location}
                        </option>
                      ))}
                  </select>
                  
                  {selectedIdCamera && <CameraPhotoCapture key={selectedIdCamera} cameraId={Number(selectedIdCamera)} label="Capture ID Photo"
                    onCapture={file => applyCapture('id', file)} />}

                  {capturedIdImage && (
                    <div className="mt-3 relative">
                      <img
                        src={capturedIdImage}
                        alt="Captured ID"
                        className="w-full rounded-lg"
                        style={{ maxHeight: '200px', objectFit: 'contain' }}
                      />
                      {idPhotoFile && (
                        <button type="button"
                          onClick={() => {
                            setCapturedIdImage(existingImages.log_license_image);
                            setIdPhotoFile(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      <p className="text-sm text-text-secondary mt-1">{idPhotoFile ? '✓ New photo captured' : 'Current ID photo'}</p>
                    </div>
                  )}
                </div>

                {/* Face Capture */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Capture Face
                  </label>
                  <select
                    value={selectedFaceCamera}
                    onChange={e => setSelectedFaceCamera(e.target.value)}
                    className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                  >
                    <option value="">Select Face Camera</option>
                    {cameras
                      .filter(camera => camera.camera_purpose === 'face_recognition')
                      .map(camera => (
                        <option key={camera.camera_id} value={camera.camera_id}>
                          {camera.name || camera.camera_location}
                        </option>
                      ))}
                  </select>
                  
                  {selectedFaceCamera && <CameraPhotoCapture key={selectedFaceCamera} cameraId={Number(selectedFaceCamera)} label="Capture Face Photo"
                    onCapture={file => applyCapture('face', file)} />}

                  {capturedFaceImage && (
                    <div className="mt-3 relative">
                      <img
                        src={capturedFaceImage}
                        alt="Captured Face"
                        className="w-full rounded-lg"
                        style={{ maxHeight: '200px', objectFit: 'contain' }}
                      />
                      {facePhotoFile && (
                        <button type="button"
                          onClick={() => {
                            setCapturedFaceImage(existingImages.log_image);
                            setFacePhotoFile(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      <p className="text-sm text-text-secondary mt-1">{facePhotoFile ? '✓ New photo captured' : 'Current face photo'}</p>
                    </div>
                  )}
                </div>

                {/* License Plate Capture */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Capture License Plate
                  </label>
                  <select
                    value={selectedLicensePlateCamera}
                    onChange={e => setSelectedLicensePlateCamera(e.target.value)}
                    className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                  >
                    <option value="">Select License Plate Camera</option>
                    {cameras
                      .filter(camera => camera.camera_purpose === 'license_plate')
                      .map(camera => (
                        <option key={camera.camera_id} value={camera.camera_id}>
                          {camera.name || camera.camera_location}
                        </option>
                      ))}
                  </select>
                  
                  {selectedLicensePlateCamera && <CameraPhotoCapture ref={licensePlateCaptureRef} key={selectedLicensePlateCamera} cameraId={Number(selectedLicensePlateCamera)} label="Capture License Plate"
                    onCapture={file => applyCapture('license_plate', file)} />}

                  {capturedLicensePlateImage && (
                    <div className="mt-3 relative">
                      <img
                        src={capturedLicensePlateImage}
                        alt="Captured License Plate"
                        className="w-full rounded-lg"
                        style={{ maxHeight: '200px', objectFit: 'contain' }}
                      />
                      {licensePlatePhotoFile && (
                        <button type="button"
                          onClick={() => {
                            setCapturedLicensePlateImage(existingImages.log_url);
                            setLicensePlatePhotoFile(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      <p className="text-sm text-text-secondary mt-1">{licensePlatePhotoFile ? '✓ New photo captured' : 'Current license plate photo'}</p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Vehicle Photos - 4 Angles */}
          {formData.has_vehicle === '1' && (
            <Card className="mb-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Vehicle Photos</h3>
              <div className="space-y-4">
                {/* 4-Angle Capture Buttons */}
                <div>
                  <p className="text-sm font-medium text-text-primary mb-2">Capture Vehicle Angles</p>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Front */}
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">Front Camera</label>
                      <div className="flex gap-2">
                        <select
                          value={selectedLicensePlateCamera}
                          onChange={(e) => setSelectedLicensePlateCamera(e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-background-primary text-gray-900"
                        >
                          <option value="">Select camera</option>
                          {cameras.filter(c => c.camera_purpose === 'vehicle_front' || c.camera_purpose === 'license_plate').map(camera => (
                            <option key={camera.camera_id} value={camera.camera_id}>
                              {camera.name || camera.camera_location || `Camera ${camera.camera_id}`}
                            </option>
                          ))}
                        </select>
                        <Button
                          type="button"
                          aria-label="Capture front vehicle photo"
                            onClick={() => captureVehicleAngle('front')}
                          disabled={!selectedLicensePlateCamera || isCapturingVehicleAngle !== null}
                          variant="outline"
                          size="sm"
                        >
                          {isCapturingVehicleAngle === 'front' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CameraIcon className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Right */}
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">Right Camera</label>
                      <div className="flex gap-2">
                        <select
                          value={selectedLicensePlateCamera}
                          onChange={(e) => setSelectedLicensePlateCamera(e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-background-primary text-gray-900"
                        >
                          <option value="">Select camera</option>
                          {cameras.filter(c => c.camera_purpose === 'vehicle_right' || c.camera_purpose === 'license_plate').map(camera => (
                            <option key={camera.camera_id} value={camera.camera_id}>
                              {camera.name || camera.camera_location || `Camera ${camera.camera_id}`}
                            </option>
                          ))}
                        </select>
                        <Button
                          type="button"
                          aria-label="Capture right vehicle photo"
                            onClick={() => captureVehicleAngle('right')}
                          disabled={!selectedLicensePlateCamera || isCapturingVehicleAngle !== null}
                          variant="outline"
                          size="sm"
                        >
                          {isCapturingVehicleAngle === 'right' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CameraIcon className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Left */}
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">Left Camera</label>
                      <div className="flex gap-2">
                        <select
                          value={selectedLicensePlateCamera}
                          onChange={(e) => setSelectedLicensePlateCamera(e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-background-primary text-gray-900"
                        >
                          <option value="">Select camera</option>
                          {cameras.filter(c => c.camera_purpose === 'vehicle_left' || c.camera_purpose === 'license_plate').map(camera => (
                            <option key={camera.camera_id} value={camera.camera_id}>
                              {camera.name || camera.camera_location || `Camera ${camera.camera_id}`}
                            </option>
                          ))}
                        </select>
                        <Button
                          type="button"
                          aria-label="Capture left vehicle photo"
                            onClick={() => captureVehicleAngle('left')}
                          disabled={!selectedLicensePlateCamera || isCapturingVehicleAngle !== null}
                          variant="outline"
                          size="sm"
                        >
                          {isCapturingVehicleAngle === 'left' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CameraIcon className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Back */}
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">Back Camera</label>
                      <div className="flex gap-2">
                        <select
                          value={selectedLicensePlateCamera}
                          onChange={(e) => setSelectedLicensePlateCamera(e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-background-primary text-gray-900"
                        >
                          <option value="">Select camera</option>
                          {cameras.filter(c => c.camera_purpose === 'vehicle_back' || c.camera_purpose === 'license_plate').map(camera => (
                            <option key={camera.camera_id} value={camera.camera_id}>
                              {camera.name || camera.camera_location || `Camera ${camera.camera_id}`}
                            </option>
                          ))}
                        </select>
                        <Button
                          type="button"
                          aria-label="Capture back vehicle photo"
                            onClick={() => captureVehicleAngle('back')}
                          disabled={!selectedLicensePlateCamera || isCapturingVehicleAngle !== null}
                          variant="outline"
                          size="sm"
                        >
                          {isCapturingVehicleAngle === 'back' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CameraIcon className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Captured Images Grid */}
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {vehicleFrontImage && (
                      <div className="relative">
                        <img src={vehicleFrontImage} alt="Front" className="w-full h-32 object-cover rounded-lg" />
                        {vehicleFrontFile && (
                          <button type="button"
                            onClick={() => {
                              setVehicleFrontImage(existingImages.vehicle_front);
                              setVehicleFrontFile(null);
                            }}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        <p className="text-xs text-center text-text-secondary mt-1">Front</p>
                      </div>
                    )}
                    {vehicleRightImage && (
                      <div className="relative">
                        <img src={vehicleRightImage} alt="Right" className="w-full h-32 object-cover rounded-lg" />
                        {vehicleRightFile && (
                          <button type="button"
                            onClick={() => {
                              setVehicleRightImage(existingImages.vehicle_right);
                              setVehicleRightFile(null);
                            }}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        <p className="text-xs text-center text-text-secondary mt-1">Right</p>
                      </div>
                    )}
                    {vehicleLeftImage && (
                      <div className="relative">
                        <img src={vehicleLeftImage} alt="Left" className="w-full h-32 object-cover rounded-lg" />
                        {vehicleLeftFile && (
                          <button type="button"
                            onClick={() => {
                              setVehicleLeftImage(existingImages.vehicle_left);
                              setVehicleLeftFile(null);
                            }}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        <p className="text-xs text-center text-text-secondary mt-1">Left</p>
                      </div>
                    )}
                    {vehicleBackImage && (
                      <div className="relative">
                        <img src={vehicleBackImage} alt="Back" className="w-full h-32 object-cover rounded-lg" />
                        {vehicleBackFile && (
                          <button type="button"
                            onClick={() => {
                              setVehicleBackImage(existingImages.vehicle_back);
                              setVehicleBackFile(null);
                            }}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        <p className="text-xs text-center text-text-secondary mt-1">Back</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Vehicle Information */}
          {formData.has_vehicle === '1' && (
            <Card>
              <h3 className="text-lg font-semibold text-text-primary mb-4">Vehicle Information</h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Input
                  label="License Plate"
                  name="log_license_plate"
                  value={formData.log_license_plate}
                  onChange={handleChange}
                  placeholder="ABC123"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                  <select
                    name="log_vehicle_type"
                    value={formData.log_vehicle_type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                  >
                    <option value="">Select Vehicle Type</option>
                    <option value="Car">Car</option>
                    <option value="SUV">SUV</option>
                    <option value="Pickup">Pickup</option>
                    <option value="Truck Only">Truck Only</option>
                    <option value="Truck with Trailer">Truck with Trailer</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <Input
                  label="Vehicle Make"
                  name="log_vehicle_make"
                  value={formData.log_vehicle_make}
                  onChange={handleChange}
                  placeholder="Toyota, Ford, etc."
                />
                <Input
                  label="Vehicle Model"
                  name="log_vehicle_model"
                  value={formData.log_vehicle_model}
                  onChange={handleChange}
                  placeholder="Camry, F-150, etc."
                />
                <Input
                  label="Vehicle Color"
                  name="log_vehicle_color"
                  value={formData.log_vehicle_color}
                  onChange={handleChange}
                  placeholder="Red, Blue, White, etc."
                />
                <Input
                  label="Truck/Unit #"
                  name="log_truck"
                  value={formData.log_truck}
                  onChange={handleChange}
                  placeholder="Unit number"
                />
                <Input
                  label="Trailer #"
                  name="log_trailer"
                  value={formData.log_trailer}
                  onChange={handleChange}
                  placeholder="Trailer number"
                />
                <Input
                  label="Trailer Plate"
                  name="log_trailer_plate"
                  value={formData.log_trailer_plate}
                  onChange={handleChange}
                  placeholder="Trailer license plate"
                />
                <Input
                  label="Damages"
                  name="log_damages"
                  value={formData.log_damages}
                  onChange={handleChange}
                  placeholder="Any visible damages"
                />
              </div>

              {/* Vehicle Inspection Details */}
              <div className="mt-4 pt-4 border-t border-border">
                <h4 className="text-md font-semibold text-text-primary mb-3">Inspection Details</h4>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <Input
                    label="Access Point"
                    name="access_point"
                    value={formData.access_point}
                    onChange={handleChange}
                    placeholder="Gate number or entry point"
                  />
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Inspection Status
                    </label>
                    <select
                      key={`inspection-${formData.inspection_status}`}
                      name="inspection_status"
                      value={formData.inspection_status}
                      onChange={handleChange}
                      className="w-full px-3 py-2 bg-background-elevated border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select Status</option>
                      <option value="PENDING">Pending</option>
                      <option value="PASSED">Passed</option>
                      <option value="FAILED">Failed</option>
                      <option value="NOT_REQUIRED">Not Required</option>
                    </select>
                  </div>
                  <Input
                    label="Inspected By"
                    name="inspected_by"
                    value={formData.inspected_by}
                    onChange={handleChange}
                    placeholder="Inspector name"
                  />
                </div>
              </div>
            </Card>
          )}

          {/* Parking Assignment */}
          {formData.has_vehicle === '1' && formData.location_id && (
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <ParkingCircle size={20} className="text-primary-500" />
                <h3 className="text-lg font-semibold text-text-primary">Parking Assignment</h3>
              </div>
              
              {loadingParkingLots ? (
                <p className="text-sm text-text-muted">Loading parking lots...</p>
              ) : parkingLots.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    No parking lots configured for this location.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Parking Lot Selection */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Parking Lot
                    </label>
                    <select
                      value={selectedParkingLot}
                      onChange={e => handleParkingLotChange(e.target.value)}
                      disabled={loadingParkingLots}
                      className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                    >
                      <option value="">Select a parking lot</option>
                      {parkingLots.map(lot => (
                        <option key={lot.id} value={lot.id}>
                          {lot.name} ({lot.available_spots}/{lot.total_spots} available)
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Parking Summary */}
                  {selectedParkingLot && parkingSummary && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <span className="font-medium">{parkingSummary.available}</span> of{' '}
                        <span className="font-medium">{parkingSummary.total}</span> spots available
                      </p>
                    </div>
                  )}

                  {/* Parking Spot Selection */}
                  {selectedParkingLot && (
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        {formData.log_vehicle_type === 'Truck with Trailer' ? 'Truck Spot #' : 'Parking Spot'}
                      </label>
                      {loadingSpots ? (
                        <p className="text-sm text-text-muted">Loading spots...</p>
                      ) : availableSpots.length === 0 ? (
                        <p className="text-sm text-orange-600">No available spots in this lot.</p>
                      ) : (
                        <select
                          value={selectedParkingSpot}
                          onChange={e => handleParkingSpotChange(e.target.value)}
                          className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                        >
                          <option value="">Select a spot</option>
                          {availableSpots.map(spot => {
                            const spotId = (spot as any).id || spot.spot_id;
                            const spotSection = (spot as any).row || spot.section;
                            const isCurrentSpot = spotId?.toString() === originalParkingSpotId;
                            return (
                              <option key={spotId} value={spotId} disabled={spotId?.toString() === selectedParkingSpot2}>
                                {spot.spot_number} - {spot.spot_type}
                                {spotSection && ` (${spotSection})`}
                                {isCurrentSpot && ' (Current)'}
                              </option>
                            );
                          })}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Second Parking Spot Selection - Only for Truck with Trailer */}
                  {selectedParkingLot && formData.log_vehicle_type === 'Truck with Trailer' && selectedParkingSpot && (
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Trailer Spot # (Optional)
                      </label>
                      <p className="text-xs text-text-muted mb-2">
                        Select a second spot for the trailer if needed.
                      </p>
                      {loadingSpots ? (
                        <p className="text-sm text-text-muted">Loading spots...</p>
                      ) : (
                        <select
                          value={selectedParkingSpot2}
                          onChange={e => setSelectedParkingSpot2(e.target.value)}
                          className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                        >
                          <option value="">No second spot needed</option>
                          {availableSpots
                            .filter(spot => {
                              const spotId = (spot as any).id || spot.spot_id;
                              return spotId?.toString() !== selectedParkingSpot; // Exclude first selected spot
                            })
                            .map(spot => {
                              const spotId = (spot as any).id || spot.spot_id;
                              const spotSection = (spot as any).row || spot.section;
                              const isCurrentSpot2 = spotId?.toString() === originalParkingSpotId2;
                              return (
                                <option key={spotId} value={spotId}>
                                  {spot.spot_number} - {spot.spot_type}
                                  {spotSection && ` (${spotSection})`}
                                  {isCurrentSpot2 && ' (Current 2nd)'}
                                </option>
                              );
                            })}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Selected Spot Info */}
                  {selectedParkingSpot && (
                    <div className={`border rounded-lg p-3 ${selectedParkingSpot !== originalParkingSpotId || selectedParkingSpot2 !== originalParkingSpotId2 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
                      {(() => {
                        const spot = availableSpots.find(s => {
                          const sId = (s as any).id || s.spot_id;
                          return sId?.toString() === selectedParkingSpot;
                        });
                        const spot2 = selectedParkingSpot2 ? availableSpots.find(s => {
                          const sId = (s as any).id || s.spot_id;
                          return sId?.toString() === selectedParkingSpot2;
                        }) : null;
                        if (!spot) return null;
                        const spotSection = (spot as any).row || spot.section;
                        const isCurrentSpot = selectedParkingSpot === originalParkingSpotId;
                        const hasChanges = selectedParkingSpot !== originalParkingSpotId || selectedParkingSpot2 !== originalParkingSpotId2;
                        const isTruckWithTrailer = formData.log_vehicle_type === 'Truck with Trailer';
                        return (
                          <div className={`text-sm ${hasChanges ? 'text-yellow-800' : 'text-green-800'}`}>
                            <p>
                              <span className="font-medium">{isTruckWithTrailer ? (isCurrentSpot ? 'Current Truck Spot:' : 'New Truck Spot:') : (isCurrentSpot ? 'Current Spot:' : 'New Spot:')}</span> {spot.spot_number}
                              {spotSection && ` in ${spotSection}`}
                              {' - '}{spot.spot_type} spot
                            </p>
                            {spot2 && (
                              <p className="mt-1">
                                <span className="font-medium">{selectedParkingSpot2 === originalParkingSpotId2 ? 'Current Trailer Spot:' : 'New Trailer Spot:'}</span> {spot2.spot_number} - {spot2.spot_type} spot
                              </p>
                            )}
                            {hasChanges && (originalParkingSpotId || originalParkingSpotId2) && (
                              <span className="block mt-2 text-yellow-600">
                                ⚠️ Changing parking spot(s) will release the old spot(s)
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Clear parking option */}
                  {originalParkingSpotId && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedParkingLot('');
                          setSelectedParkingSpot('');
                          setSelectedParkingSpot2('');
                          setAvailableSpots([]);
                        }}
                        className="text-sm text-red-600 hover:text-red-800 underline"
                      >
                        Remove parking assignment
                      </button>
                      <span className="text-xs text-gray-500">(This will free the current spot{originalParkingSpotId2 ? 's' : ''})</span>
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Driver/Visitor Information */}
          <Card>
            <h3 className="text-lg font-semibold text-text-primary mb-4">Driver/Visitor Information</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Input
                label="Driver/Visitor Name"
                name="log_external_driver_name"
                value={formData.log_external_driver_name}
                onChange={handleChange}
                placeholder="Full name"
              />
              <Input
                label="Driver License Number"
                name="log_driver_license_no"
                value={formData.log_driver_license_no}
                onChange={handleChange}
                placeholder="License number"
              />
              <Input
                label="Email"
                name="log_driver_email"
                type="email"
                value={formData.log_driver_email}
                onChange={handleChange}
                placeholder="email@example.com"
              />
              <Input
                label="Visitor Company"
                name="log_visitor_company"
                value={formData.log_visitor_company}
                onChange={handleChange}
                placeholder="Company name"
              />
              <Input
                label="Phone Number"
                name="log_visitor_phone_number"
                value={formData.log_visitor_phone_number}
                onChange={handleChange}
                placeholder="(555) 123-4567"
              />
              <Input
                label="Visit Purpose"
                name="log_visitor_purpose"
                value={formData.log_visitor_purpose}
                onChange={handleChange}
                placeholder="Reason for visit"
              />
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Safety Vest
                </label>
                <select
                  key={`safety-${formData.log_safety_vest}`}
                  name="log_safety_vest"
                  value={formData.log_safety_vest}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background-elevated border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select</option>
                  <option value="YES">Yes</option>
                  <option value="NO">No</option>
                </select>
              </div>
            </div>
          </Card>

          {/* Notes */}
          <Card>
            <h3 className="text-lg font-semibold text-text-primary mb-4">Notes</h3>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Additional Notes
              </label>
              <textarea
                name="log_note"
                value={formData.log_note}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 bg-background-elevated border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                placeholder="Any additional information..."
              />
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/visitor-logs')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSaving}
              disabled={isSaving}
            >
              {isSaving ? 'Updating...' : 'Update Log'}
            </Button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default EditEntryExitPage;
